import clsx from 'clsx'
import React, { useState } from 'react'
import {
  Control,
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import { SearchCityDatesAdultsModeEnum } from './search-city-dates-adults.enum'
import { getSearchCityDatesAdultClassNameByEnum } from './search-city-dates-adults.utils'
import { Button } from '../../atoms'
import { useTravelTypeContext } from '../../contexts'
import { AdultsPicker, Checkbox, DatePicker } from '../../forms'
import { AdultsPickerModeEnum } from '../../forms/adults-picker/adults-picker.enum'
import { DatePickerModeEnum } from '../../forms/date-picker/date-picker.enum'
import { LocationPicker } from '../../organisms'
import { LocationPickerModeEnum } from '../../organisms/location-picker/location-picker.enum'
import { TravelTypeEnum } from '../../../lib/graphql'
import { breakpoints, useMediaQuery } from '../../../lib/hooks'
import { Mixpanel } from '../../../utils/Mixpanel'

interface ISearchCityDatesAdultsProps<T extends FieldValues> {
  control: Control<T, any>
  setValue: UseFormSetValue<T>
  getValues: UseFormGetValues<T>
  searchClick: () => void
  mode?: SearchCityDatesAdultsModeEnum
  isLoading: boolean
  isHeader?: boolean
}

export const SearchCityDatesAdults = <T extends FieldValues>({
  control,
  setValue,
  getValues,
  searchClick,
  mode = SearchCityDatesAdultsModeEnum.DEFAULT,
  isLoading,
  isHeader = false,
}: ISearchCityDatesAdultsProps<T>) => {
  const isMobile = useMediaQuery(breakpoints.newMobile)

  const [activeStartDate, setActiveStartDate] = useState<Date>(new Date())

  const { travelType } = useTravelTypeContext()

  const shouldDisplayPrivatisedCheckBox =
    travelType !== TravelTypeEnum.CORPORATE_EVENING_EVENT

  return (
    <div
      data-testid='search-bar'
      className={clsx(s[getSearchCityDatesAdultClassNameByEnum(mode)], {
        [s.mobile]: isMobile,
        [s.houseSearchHeader]: isHeader,
      })}
    >
      <div className={s.placePickerContainer}>
        <LocationPicker
          control={control}
          rules={{ required: false }}
          name='location'
          className={s.placePicker}
          disabled={false}
          isMobile={isMobile}
          locationTypes={[
            'locality',
            'administrative_area_level_1',
            'administrative_area_level_2',
            'colloquial_area',
            'natural_feature',
          ]}
          alternativeClassName='alternativeAi'
          mode={LocationPickerModeEnum.HOUSE_SEARCH}
        />
      </div>
      {!isHeader && (
        <>
          <div className={s.datePickerContainer} data-testid='dates-picker'>
            <DatePicker
              hideLabels
              control={control}
              rules={{}}
              name='dates'
              getAndSetter={{
                setValue,
                getValues,
              }}
              className={s.datesPicker}
              onClose={() => {
                return
              }}
              dateCalendarProps={{
                activeStartDate,
                setActiveStartDate,
                houseAvailabilities: [],
              }}
              isMobile={isMobile}
              alternativeClassName='alternativeAi'
              mode={DatePickerModeEnum.HOUSE_SEARCH}
            />
          </div>
          <div className={s.adultsPickerContainer}>
            <AdultsPicker
              control={control}
              rules={{}}
              name='adults'
              className={s.adultsPicker}
              isMobile={isMobile}
              alternativeClassName='alternativeAi'
              mode={AdultsPickerModeEnum.HOUSE_SEARCH}
            />
          </div>

          {shouldDisplayPrivatisedCheckBox ? (
            <div className={s.checkboxContainer}>
              <Checkbox
                control={control}
                rules={{}}
                name='houseCanBePrivatised'
                className={s.checkbox}
                label={
                  <FormattedMessage
                    id='ui.SearchCityDatesAdults.label.privateHouses'
                    defaultMessage='Voir uniquement les lieux privatisés'
                  />
                }
                onClick={() => {
                  Mixpanel.track('house_privatised')
                }}
              />
            </div>
          ) : null}
        </>
      )}
      <div>
        <Button
          className={s.searchButton}
          onClick={searchClick}
          loading={isLoading}
          disabled={isLoading === true}
          dataMixpanel={{ name: 'Submit Search', data: getValues() }}
          data-testid='search-button'
        >
          <FormattedMessage
            id='ui.SearchCityDatesAdults.search'
            defaultMessage='Rechercher'
          />
        </Button>
      </div>
    </div>
  )
}
